import axios from "axios";

const baseURL =
  process.env.REACT_APP_API_URL || "https://hhcourses-backend.vercel.app/";

const api = axios.create({
  baseURL,
});

/**
 * Stale-while-revalidate cache for GET requests.
 *
 * React Router unmounts a screen when you navigate away, so without this every tab
 * switch re-runs the same requests from scratch and the user sits behind a loading
 * animation. Screens read whatever is already cached to paint immediately, then let the
 * refreshed response update them in place.
 */
const DEFAULT_TTL = 60000;
// Without a timeout a dropped connection never settles, and because identical requests
// are shared, every later caller for that URL would wait on it forever.
const DEFAULT_TIMEOUT = 15000;

const cache = new Map(); // key -> { data, ts }
const inflight = new Map(); // key -> Promise, so concurrent identical GETs share one request

const keyFor = (url, params) =>
  params ? `${url}?${JSON.stringify(params)}` : url;

/**
 * Synchronously read a cached response, for initializing state on first render.
 * Returns undefined when nothing has been cached for this request yet.
 */
export function readCache(url, params, ttl = DEFAULT_TTL) {
  const entry = cache.get(keyFor(url, params));
  if (!entry) return undefined;
  return { data: entry.data, isStale: Date.now() - entry.ts > ttl };
}

/**
 * GET with caching and in-flight deduplication.
 * Resolves from cache when the entry is still fresh, otherwise fetches. Concurrent calls
 * for the same url+params share a single network request.
 */
export function cachedGet(
  url,
  { params, ttl = DEFAULT_TTL, force = false, timeout = DEFAULT_TIMEOUT } = {}
) {
  const key = keyFor(url, params);
  const entry = cache.get(key);

  if (!force && entry && Date.now() - entry.ts <= ttl) {
    return Promise.resolve(entry.data);
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const request = api
    .get(url, { params, timeout })
    .then((response) => {
      cache.set(key, { data: response.data, ts: Date.now() });
      inflight.delete(key);
      return response.data;
    })
    .catch((error) => {
      inflight.delete(key);
      throw error;
    });

  inflight.set(key, request);
  return request;
}

// Course content only changes when an admin edits it, so a learner's own actions can
// never make these stale. Everything else is dropped after a write.
const STATIC_READS = ["/coursesData", "sideBarData"];

// A write can affect progress, chapter progress, streaks, lesson counts and leaderboards
// at once. Rather than maintain a mapping of every mutation to the reads it touches,
// invalidate broadly and let the next read refill. Centralizing it here means no call
// site can forget to do it after posting.
api.interceptors.response.use((response) => {
  const method = (response.config?.method || "get").toLowerCase();
  if (method !== "get") {
    for (const key of Array.from(cache.keys())) {
      if (!STATIC_READS.some((prefix) => key.includes(prefix))) cache.delete(key);
    }
  }
  return response;
});

/**
 * Kick off a request purely to warm the cache. Failures are ignored, since a prefetch
 * that misses just means the screen fetches normally when it opens.
 */
export function prefetch(url, options) {
  cachedGet(url, options).catch(() => {});
}

/**
 * Drop cached entries whose url starts with the given prefix, or everything when called
 * with no argument. Use after a mutation so the next read refetches.
 */
export function invalidate(urlPrefix) {
  if (!urlPrefix) {
    cache.clear();
    return;
  }
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(urlPrefix)) cache.delete(key);
  }
}

export default api;
